import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  CalendarMonthRounded as CalendarIcon,
  CameraAltRounded as CameraIcon,
  CloseRounded as CloseIcon,
  InsertDriveFileOutlined as FileIcon,
  LocalGasStationRounded as FuelIcon,
  OpacityRounded as MeasurementIcon,
  PhotoLibraryRounded as GalleryIcon,
} from "@mui/icons-material";
import { getCurrentUser } from "../../../data/user";
import FileView from "../../partial/FileView";

type EntryKind = "measurement" | "bunkering";

type MobileGasoilEntryDialogProps = {
  open: boolean;
  kind: EntryKind;
  vesselId?: string | number | null;
  initialData?: any;
  supplierOptions?: string[];
  onClose: () => void;
  onSaved: () => Promise<void> | void;
};

const API_URL = process.env.REACT_APP_API_URL;
const APP_URL = process.env.REACT_APP_URL || "";

const getId = (value: any) => {
  const source = typeof value === "string" ? value : value?.["@id"] || value?.id;
  return source ? String(source).split("/").filter(Boolean).pop() : "";
};

const getUserIri = (user: any) => {
  if (!user) return "";
  if (typeof user === "string" && user.startsWith("/api/")) return user;
  return user["@id"] || (user.id ? `/api/users/${user.id}` : "");
};

const authHeaders = (json = true) => ({
  ...(json ? { "Content-Type": "application/ld+json; charset=utf-8" } : {}),
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const MobileGasoilEntryDialog: React.FC<MobileGasoilEntryDialogProps> = ({
  open,
  kind,
  vesselId,
  initialData,
  supplierOptions = [],
  onClose,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentUser = useSelector((state: any) => state.user?.data);
  const cameraInput = useRef<HTMLInputElement | null>(null);
  const galleryInput = useRef<HTMLInputElement | null>(null);
  const previewObjectUrl = useRef("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [supplier, setSupplier] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [error, setError] = useState("");
  const editing = Boolean(getId(initialData));
  const isBunkering = kind === "bunkering";
  const existingFile = initialData?.file;
  const existingFileName =
    existingFile?.originalName ||
    existingFile?.description ||
    existingFile?.name ||
    existingFile?.fileName ||
    existingFile?.filename ||
    (typeof existingFile === "string" ? existingFile.split("/").filter(Boolean).pop() : "") ||
    t("existingDocument", "Existing document");
  const existingFileSource =
    (typeof existingFile === "object"
      ? existingFile?.contentUrl || existingFile?.url || existingFile?.path || existingFile?.["@id"]
      : existingFile) || "";
  const existingFileUrl = existingFileSource
    ? /^(https?:|blob:|data:)/i.test(existingFileSource)
      ? existingFileSource
      : `${APP_URL}${existingFileSource.startsWith("/") ? "" : "/"}${existingFileSource}`
    : "";
  const availableSuppliers = useMemo(
    () =>
      Array.from(
        new Set([initialData?.supplier, ...supplierOptions].map((value) => String(value || "").trim()).filter(Boolean))
      ),
    [initialData?.supplier, supplierOptions]
  );

  const title = useMemo(() => {
    if (isBunkering) return editing ? t("editBunkering", "Edit bunkering") : t("addBunkering", "Add bunkering");
    return editing ? t("editGasoilMeasurement", "Edit gasoil measurement") : t("addGasoilMeasurement", "Add gasoil measurement");
  }, [editing, isBunkering, t]);

  useEffect(() => {
    if (!open) return;
    setAmount(initialData?.amount != null ? String(initialData.amount) : "");
    setDate(
      initialData?.date
        ? dayjs(initialData.date).format(isBunkering ? "YYYY-MM-DD" : "YYYY-MM-DDTHH:mm")
        : dayjs().format(isBunkering ? "YYYY-MM-DD" : "YYYY-MM-DDTHH:mm")
    );
    setSupplier(initialData?.supplier || "");
    setFile(null);
    setPreviewOpen(false);
    setPreviewFile(null);
    setPreviewLoading(false);
    setPreviewError("");
    if (previewObjectUrl.current) {
      URL.revokeObjectURL(previewObjectUrl.current);
      previewObjectUrl.current = "";
    }
    setError("");
    if (!currentUser) dispatch<any>(getCurrentUser());
  }, [open, initialData, isBunkering, currentUser, dispatch]);

  useEffect(() => {
    return () => {
      if (previewObjectUrl.current) {
        URL.revokeObjectURL(previewObjectUrl.current);
      }
    };
  }, []);

  const fetchUserIri = async () => {
    const existing = getUserIri(currentUser);
    if (existing) return existing;
    const response = await axios.get(`${API_URL}/users?page=1`, { headers: authHeaders(false) });
    const user = response.data?.["hydra:member"]?.[0] || response.data?.member?.[0];
    return getUserIri(user);
  };

  const uploadFile = async () => {
    if (!file) return initialData?.file?.["@id"] || (typeof initialData?.file === "string" ? initialData.file : null);
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(`${API_URL}/files`, formData, { headers: authHeaders(false) });
    return response.data?.["@id"] || null;
  };

  const save = async () => {
    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
      setError(t("invalidAmount", "Please enter a valid amount."));
      return;
    }
    if (!date || !vesselId) {
      setError(t("missingRequiredFields", "Complete the required fields."));
      return;
    }

    try {
      setSaving(true);
      setError("");
      const vessel = String(vesselId).startsWith("/api/") ? String(vesselId) : `/api/vessels/${getId(vesselId)}`;
      let payload: any;
      let resource: string;

      if (isBunkering) {
        payload = {
          amount: parsedAmount,
          date: dayjs(date).startOf("day").toISOString(),
          supplier: supplier.trim() || null,
          vessel,
          file: await uploadFile(),
        };
        resource = "vessel_bunkerings";
      } else {
        const user = await fetchUserIri();
        payload = { amount: parsedAmount, date: dayjs(date).toISOString(), vessel, user };
        resource = "vessel_gasoils";
      }

      const iri = initialData?.["@id"];
      if (editing) {
        await axios.put(`${APP_URL}${iri || `/api/${resource}/${getId(initialData)}`}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API_URL}/${resource}`, payload, { headers: authHeaders() });
      }

      await onSaved();
      onClose();
    } catch (exception: any) {
      setError(exception?.response?.data?.detail || exception?.response?.data?.["hydra:description"] || exception?.message || t("saveFailed", "Saving failed."));
    } finally {
      setSaving(false);
    }
  };

  const chooseFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] || null);
  };

  const getAbsoluteUrl = (value: string) => {
    if (/^(https?:|blob:|data:)/i.test(value)) return value;
    return `${APP_URL}${value.startsWith("/") ? "" : "/"}${value}`;
  };

  const openExistingFilePreview = async () => {
    if (!existingFile) return;

    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError("");
    setPreviewFile(null);

    try {
      let fileData = typeof existingFile === "object" ? existingFile : null;
      let source = existingFileSource;

      if (!fileData || (!fileData.contentUrl && source.includes("/api/files/"))) {
        const metadataResponse = await axios.get(getAbsoluteUrl(source), {
          headers: authHeaders(false),
        });
        fileData = metadataResponse.data;
        source = fileData?.contentUrl || fileData?.url || fileData?.path || "";
      }

      if (!source) {
        throw new Error(t("filePreviewLoadFailed", "The file preview could not be loaded."));
      }

      if (/^(blob:|data:)/i.test(source)) {
        setPreviewFile({
          ...(fileData || {}),
          description: existingFileName,
          contentUrl: source,
        });
        return;
      }

      const fileResponse = await axios.get(getAbsoluteUrl(source), {
        headers: authHeaders(false),
        responseType: "blob",
      });

      if (previewObjectUrl.current) {
        URL.revokeObjectURL(previewObjectUrl.current);
      }

      previewObjectUrl.current = URL.createObjectURL(fileResponse.data);
      setPreviewFile({
        ...(fileData || {}),
        description: existingFileName,
        contentUrl: previewObjectUrl.current,
        contentType: fileResponse.data?.type,
      });
    } catch (exception: any) {
      setPreviewError(
        exception?.response?.data?.message ||
          exception?.message ||
          t("filePreviewLoadFailed", "The file preview could not be loaded.")
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeFilePreview = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
    setPreviewError("");
    if (previewObjectUrl.current) {
      URL.revokeObjectURL(previewObjectUrl.current);
      previewObjectUrl.current = "";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      className="mobile-gasoil-entry-dialog"
      PaperProps={{ className: "mobile-gasoil-entry-paper" }}
    >
      <Box className="mobile-sheet-handle" />
      <IconButton className="mobile-sheet-close" onClick={onClose} disabled={saving} aria-label={t("close", "Close")}><CloseIcon /></IconButton>
      <Box className={`mobile-gasoil-dialog-icon${isBunkering ? " is-bunkering" : ""}`}>
        {isBunkering ? <FuelIcon /> : <MeasurementIcon />}
      </Box>
      <Typography component="h2" className="mobile-gasoil-dialog-title">{title}</Typography>

      <Box className="mobile-gasoil-form">
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label={t("amountLiters", "Amount in liters")}
          type="number"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          disabled={saving}
          fullWidth
          InputProps={{ startAdornment: isBunkering ? <FuelIcon className="mobile-field-icon" /> : <MeasurementIcon className="mobile-field-icon" /> }}
        />
        <TextField
          label={isBunkering ? t("date", "Date") : t("dateTime", "Date and time")}
          type={isBunkering ? "date" : "datetime-local"}
          value={date}
          onChange={(event) => setDate(event.target.value)}
          disabled={saving}
          fullWidth
          InputLabelProps={{ shrink: true }}
          InputProps={{ startAdornment: <CalendarIcon className="mobile-field-icon" /> }}
        />
        {isBunkering && (
          <Autocomplete
            freeSolo
            options={availableSuppliers}
            value={supplier}
            inputValue={supplier}
            onChange={(_, value) => setSupplier(value || "")}
            onInputChange={(_, value) => setSupplier(value)}
            disabled={saving}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("supplier", "Supplier")}
                placeholder={t("typeOrSelectSupplier", "Type or select a supplier")}
                fullWidth
              />
            )}
          />
        )}

        {isBunkering && (
          <Box className="mobile-upload-zone">
            <input ref={cameraInput} hidden type="file" accept="image/*,application/pdf" capture="environment" onChange={chooseFile} />
            <input ref={galleryInput} hidden type="file" accept="image/*,application/pdf" onChange={chooseFile} />
            {editing && existingFile && !file ? (
              <button type="button" className="mobile-existing-bunkering-file" onClick={openExistingFilePreview} disabled={!existingFileUrl || previewLoading}>
                {previewLoading ? <CircularProgress size={28} /> : <FileIcon />}
                <span><small>{t("currentFile", "Current file")}</small><strong>{existingFileName}</strong></span>
              </button>
            ) : (
              <span className="mobile-upload-icon"><CameraIcon /></span>
            )}
            <strong>{file?.name || (editing && existingFile ? t("replaceFile", "Replace file") : t("takeOrUploadDocument", "Take a photo or upload a document"))}</strong>
            <span>{t("uploadBunkeringReceipt", "Photograph the bunkering receipt or upload an image or PDF")}</span>
            <Box className="mobile-upload-actions">
              <Button startIcon={<CameraIcon />} onClick={() => cameraInput.current?.click()}>{t("takePhoto", "Take photo")}</Button>
              <Button startIcon={<GalleryIcon />} onClick={() => galleryInput.current?.click()}>{t("chooseFile", "Choose file")}</Button>
            </Box>
          </Box>
        )}
      </Box>

      <Box className="mobile-sheet-actions">
        <Button onClick={onClose} disabled={saving}>{t("cancel", "Cancel")}</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? <CircularProgress size={18} color="inherit" /> : t("save", "Save")}
        </Button>
      </Box>

      <Dialog open={previewOpen} onClose={closeFilePreview} fullScreen className="mobile-file-preview-dialog">
        <DialogTitle className="mobile-file-preview-title">
          <Box><Typography>{t("viewFile", "View file")}</Typography><span>{existingFileName}</span></Box>
          <IconButton onClick={closeFilePreview} aria-label={t("close", "Close")}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers className="mobile-file-preview-content">
          {previewLoading ? (
            <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
              <Box sx={{ textAlign: "center" }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>{t("loadingFilePreview", "Loading file preview...")}</Typography>
              </Box>
            </Box>
          ) : null}
          {previewError ? <Alert severity="error">{previewError}</Alert> : null}
          {!previewLoading && previewFile ? <FileView file={previewFile} includeButtons={false} /> : null}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default MobileGasoilEntryDialog;
